import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateblog } from '../../../FeatureRedux/blogs/UpdateBlogReducer';
import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';

const EditBlog = ({ blog, closeModal }) => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        title: '',
        readTime: '',
        date: '',
        imageFile: null,
        link: '',
    });

    const [errors, setErrors] = useState({});
    const [shake, setShake] = useState(false);

    useEffect(() => {
        if (blog) {
            setFormData({
                title: blog.title || '',
                readTime: blog.readTime || '',
                date: blog.date ? new Date(blog.date).toISOString().slice(0, 10) : '',
                imageFile: blog.image || null,
                link: blog.url || '',
            });
        }
    }, [blog]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData((prev) => ({ ...prev, imageFile: file }));
            setErrors((prev) => ({ ...prev, imageFile: '' }));
        } else {
            setErrors((prev) => ({ ...prev, imageFile: 'Please select a valid image file.' }));
            setShake(true);
            setTimeout(() => setShake(false), 300);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = true;
        if (!formData.readTime.trim()) newErrors.readTime = true;
        if (!formData.date.trim()) newErrors.date = true;
        if (!formData.link.trim()) newErrors.link = true;

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setShake(true);
            setTimeout(() => setShake(false), 300);
            return;
        }

        const payload = {
            id: blog._id,
            title: formData.title.trim(),
            readTime: formData.readTime.trim(),
            date: formData.date,
            url: formData.link.trim(),
            imageFile: formData.imageFile,
        };

        await dispatch(updateblog({ payload }));
        dispatch(getblog());
        closeModal();
    };

    return (
        <div className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title */}
                    <div className="col-span-2">
                        <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="title">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            placeholder="Enter blog title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-gray-700 text-white placeholder-white border ${errors.title ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                        />
                        {errors.title && <p className="mt-1 text-xs text-red-500">Title is required</p>}
                    </div>

                    {/* Read Time */}
                    <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="readTime">
                            Read Time <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="readTime"
                            placeholder="e.g. 5 min read"
                            name="readTime"
                            value={formData.readTime}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-gray-700 border text-white placeholder-white ${errors.readTime ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                        />
                        {errors.readTime && <p className="mt-1 text-xs text-red-500">Read time is required</p>}
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="date">
                            Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-gray-700 border text-white placeholder-white ${errors.date ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                        />
                        {errors.date && <p className="mt-1 text-xs text-red-500">Date is required</p>}
                    </div>

                    {/* Image Upload */}
                    <div className="col-span-2">
                        <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="image">
                            Image
                        </label>
                        <div className="flex items-center">
                            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-gray-500 transition-colors">
                                <div className="flex flex-col items-center justify-center pt-3 pb-4">
                                    <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                    </svg>
                                    <p className="mb-1 text-xs text-gray-400">
                                        <span className="font-semibold">Click to upload</span>
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formData.imageFile ?
                                            (formData.imageFile.name || 'Current image') :
                                            'PNG, JPG, GIF'}
                                    </p>
                                </div>
                                <input
                                    id="image"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    </div>

                    {/* Link */}
                    <div className="col-span-2">
                        <label className="block text-white text-sm font-medium mb-1" htmlFor="link">
                            Link <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="link"
                            placeholder="Enter blog URL"
                            name="link"
                            value={formData.link}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-gray-700 text-white border ${errors.link ? 'border-red-500 animate-shake' : 'border-gray-600'
                                } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                        />
                        {errors.link && (
                            <p className="mt-1 text-xs text-red-500">Link is required</p>
                        )}
                    </div>

                </div>

                <div className="flex justify-end space-x-3 pt-2">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 text-sm border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Update Blog
                    </button>
                </div>
            </form>

            <style>
                {`
                @keyframes shake {
                    0% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    50% { transform: translateX(5px); }
                    75% { transform: translateX(-5px); }
                    100% { transform: translateX(0); }
                }
                .animate-shake {
                    animation: shake 0.3s ease-in-out;
                }
                `}
            </style>
        </div>
    );
};

export default EditBlog;
// import React, { useState, useEffect } from 'react';
// import { useDispatch } from 'react-redux';
// import { updateblog } from '../../../FeatureRedux/blogs/UpdateBlogReducer';
// import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';

// const EditBlog = ({ blog , closeModal}) => {
//     const dispatch = useDispatch();

//     const [formData, setFormData] = useState({
//         title: '',
//         readTime: '',
//         date: '',
//         imageFile: null,
//         link: '',
//     });

//     const [errors, setErrors] = useState({});
//     const [shake, setShake] = useState(false);

//     useEffect(() => {
//         if (blog) {
//             setFormData({
//                 title: blog.title || '',
//                 readTime: blog.readTime || '',
//                 date: blog.date ? new Date(blog.date).toISOString().slice(0, 10) : '',
//                 imageFile: null,
//                 link: blog.url || '',
//             });
//         }
//     }, [blog]);

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setFormData((prev) => ({ ...prev, [name]: value }));
//         setErrors((prev) => ({ ...prev, [name]: '' }));
//     };

//     const handleImageUpload = (e) => {
//         const file = e.target.files[0];
//         if (file && file.type.startsWith('image/')) {
//             setFormData((prev) => ({ ...prev, imageFile: file }));
//             setErrors((prev) => ({ ...prev, imageFile: '' }));
//         } else {
//             setErrors((prev) => ({ ...prev, imageFile: 'Please select a valid image file.' }));
//             setShake(true);
//             setTimeout(() => setShake(false), 300);
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         const newErrors = {};
//         if (!formData.title.trim()) newErrors.title = true;
//         if (!formData.readTime.trim()) newErrors.readTime = true;
//         if (!formData.date.trim()) newErrors.date = true;
//         if (!formData.link.trim()) newErrors.link = true;

//         setErrors(newErrors);

//         if (Object.keys(newErrors).length > 0) {
//             setShake(true);
//             setTimeout(() => setShake(false), 300);
//             return;
//         }

//         const payload = {
//             id: blog._id,
//             title: formData.title.trim(),
//             readTime: formData.readTime.trim(),
//             date: formData.date,
//             url: formData.link.trim(),
//             imageFile: formData.imageFile,
//         };

//         // console.log("Dispatching update payload:", payload);
//         await dispatch(updateblog({payload}));
//         dispatch(getblog());
//         closeModal();
//     };

//     return (
//         <div className="flex flex-col items-center p-4">
//             <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-xl">
//                 <form onSubmit={handleSubmit} className="space-y-4">
//                     <input
//                         type="text"
//                         placeholder="Title"
//                         name="title"
//                         value={formData.title}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.title ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="text"
//                         placeholder="Read Time"
//                         name="readTime"
//                         value={formData.readTime}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.readTime ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="date"
//                         name="date"
//                         value={formData.date}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.date ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="file"
//                         accept="image/*"
//                         onChange={handleImageUpload}
//                         className={`w-full p-2 border rounded-md ${errors.imageFile ? 'border-red-500 animate-shake' : ''}`}
//                     />
//                     {formData.imageFile && (
//                         <p className="text-sm text-gray-600">Selected File: {formData.imageFile.name}</p>
//                     )}

//                     <input
//                         type="text"
//                         placeholder="Link"
//                         name="link"
//                         value={formData.link}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.link ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <button
//                         type="submit"
//                         className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//                     >
//                         Update Blog
//                     </button>
//                 </form>
//             </div>

//             <style>
//                 {`
//                 @keyframes shake {
//                     0% { transform: translateX(0); }
//                     25% { transform: translateX(-5px); }
//                     50% { transform: translateX(5px); }
//                     75% { transform: translateX(-5px); }
//                     100% { transform: translateX(0); }
//                 }
//                 .animate-shake {
//                     animation: shake 0.3s ease-in-out;
//                 }
//                 `}
//             </style>
//         </div>
//     );
// };

// export default EditBlog;


// import React, { useState, useEffect } from 'react';
// import { useDispatch } from 'react-redux'; // ← ADD THIS
// import { updateblog } from '../../../FeatureRedux/blogs/UpdateBlogReducer';

// const EditBlog = ({ blog }) => {
//     const dispatch = useDispatch(); // ← ADD THIS

//     const [formData, setFormData] = useState({
//         title: '',
//         readTime: '',
//         date: '',
//         imageFile: null,
//         link: '',
//     });

//     const [errors, setErrors] = useState({});
//     const [shake, setShake] = useState(false);

//     useEffect(() => {
//         if (blog) {
//             setFormData({
//                 title: blog.title || '',
//                 readTime: blog.readTime || '',
//                 date: blog.date || '',
//                 imageFile: null,
//                 link: blog.url || '',
//             });
//         }
//     }, [blog]);

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setFormData((prev) => ({ ...prev, [name]: value }));
//         setErrors((prev) => ({ ...prev, [name]: '' }));
//     };

//     const handleImageUpload = (e) => {
//         const file = e.target.files[0];
//         if (file && file.type.startsWith('image/')) {
//             setFormData((prev) => ({ ...prev, imageFile: file }));
//             setErrors((prev) => ({ ...prev, imageFile: '' }));
//         } else {
//             setErrors((prev) => ({ ...prev, imageFile: 'Please select a valid image file.' }));
//             setShake(true);
//             setTimeout(() => setShake(false), 300);
//         }
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();

//         const newErrors = {};
//         if (!formData.title.trim()) newErrors.title = true;
//         if (!formData.readTime.trim()) newErrors.readTime = true;
//         if (!formData.date.trim()) newErrors.date = true;
//         if (!formData.link.trim()) newErrors.link = true;

//         setErrors(newErrors);

//         if (Object.keys(newErrors).length > 0) {
//             setShake(true);
//             setTimeout(() => setShake(false), 300);
//             return;
//         }

//         const updatedBlog = {
//             id: blog._id, // Add ID or primary identifier here if needed
//             title: formData.title,
//             readTime: formData.readTime,
//             date: formData.date,
//             url: formData.link,
//             imageFile: formData.imageFile,
//         };

//         console.log('Dispatching update:', updatedBlog);
//         dispatch(updateblog(updatedBlog)); // ← ADD DISPATCH HERE
//     };

//     return (
//         <div className="flex flex-col items-center p-4">
//             <div className="bg-white p-6 rounded-lg shadow-md w-full">
//                 <form onSubmit={handleSubmit} className="space-y-4">
//                     <input
//                         type="text"
//                         placeholder="Title"
//                         name="title"
//                         value={formData.title}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.title ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="text"
//                         placeholder="Read Time"
//                         name="readTime"
//                         value={formData.readTime}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.readTime ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="date"
//                         name="date"
//                         value={formData.date ? new Date(formData.date).toISOString().slice(0, 10) : ''}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.date ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <input
//                         type="file"
//                         accept="image/*"
//                         onChange={handleImageUpload}
//                         className={`w-full p-2 border rounded-md ${errors.imageFile ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     {formData.imageFile && (
//                         <p className="text-sm text-gray-600">Selected File: {formData.imageFile.name || 'Existing Image'}</p>
//                     )}

//                     <input
//                         type="text"
//                         placeholder="Link"
//                         name="link"
//                         value={formData.link}
//                         onChange={handleChange}
//                         className={`w-full p-2 border rounded-md ${errors.link ? 'border-red-500 animate-shake' : ''}`}
//                     />

//                     <button
//                         type="submit"
//                         className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//                     >
//                         Update Blog
//                     </button>
//                 </form>
//             </div>

//             <style>
//                 {`
//                 @keyframes shake {
//                     0% { transform: translateX(0); }
//                     25% { transform: translateX(-5px); }
//                     50% { transform: translateX(5px); }
//                     75% { transform: translateX(-5px); }
//                     100% { transform: translateX(0); }
//                 }
//                 .animate-shake {
//                     animation: shake 0.3s ease-in-out;
//                 }
//             `}
//             </style>
//         </div>
//     );
// };

// export default EditBlog;


